export const onlyDigits = (value?: string | null): string =>
  value ? String(value).replace(/\D/g, "") : "";

export const validateEmail = (email?: string | null): boolean => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

export const validatePhone = (phone?: string | null): boolean => {
  const digits = onlyDigits(phone);
  return digits.length === 10 || digits.length === 11;
};

export const validateCEP = (cep?: string | null): boolean =>
  onlyDigits(cep).length === 8;

export const validateUrl = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

export const validateCPF = (cpf?: string | null): boolean => {
  const digits = onlyDigits(cpf);
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const calcCheckDigit = (base: string, factor: number): number => {
    let total = 0;
    for (const digit of base) {
      total += Number(digit) * factor--;
    }
    const remainder = (total * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  const firstDigit = calcCheckDigit(digits.slice(0, 9), 10);
  const secondDigit = calcCheckDigit(digits.slice(0, 10), 11);

  return firstDigit === Number(digits[9]) && secondDigit === Number(digits[10]);
};

export const validateCNPJ = (cnpj?: string | null): boolean => {
  if (!cnpj) return false;
  const normalized = String(cnpj)
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");
  if (normalized.length !== 14) return false;
  // 12 posições alfanuméricas + 2 dígitos verificadores numéricos
  if (!/^[0-9A-Z]{12}\d{2}$/.test(normalized)) return false;
  if (/^(.)\1{13}$/.test(normalized)) return false;

  const charValue = (char: string): number => char.charCodeAt(0) - 48;

  const calcCheckDigit = (base: string): number => {
    const weights =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let total = 0;
    for (let i = 0; i < base.length; i++) {
      total += charValue(base[i]) * weights[i];
    }
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calcCheckDigit(normalized.slice(0, 12));
  const secondDigit = calcCheckDigit(normalized.slice(0, 13));

  return (
    firstDigit === Number(normalized[12]) &&
    secondDigit === Number(normalized[13])
  );
};

// Validadores de campo para feedback ao vivo: retornam a mensagem de erro
// ou null se válido. Campos vazios retornam null (a obrigatoriedade é tratada
// pela prop `required` do input) — aqui validamos apenas o formato.
export type FieldValidator = (value: string) => string | null;

export const emailField: FieldValidator = (v) =>
  !v.trim() ? null : validateEmail(v) ? null : "E-mail inválido";

export const phoneField: FieldValidator = (v) =>
  !v.trim() ? null : validatePhone(v) ? null : "Telefone inválido";

export const cepField: FieldValidator = (v) =>
  !v.trim() ? null : validateCEP(v) ? null : "CEP inválido";

export const urlField: FieldValidator = (v) =>
  !v.trim() ? null : validateUrl(v) ? null : "Endereço inválido";

export const cpfField: FieldValidator = (v) =>
  !v.trim() ? null : validateCPF(v) ? null : "CPF inválido";

export const cnpjField: FieldValidator = (v) =>
  !v.trim() ? null : validateCNPJ(v) ? null : "CNPJ inválido";

export const minLenField =
  (n: number, message?: string): FieldValidator =>
  (v) =>
    !v ? null : v.length >= n ? null : message ?? `Mínimo de ${n} caracteres`;

const isLocalApi = (): boolean => {
  try {
    const hostname = new URL(process.env.NODE_API_URL ?? "").hostname;
    return ["localhost", "127.0.0.1", "::1"].includes(hostname);
  } catch {
    return false;
  }
};

export const strongPasswordMessage =
  "A senha deve ter ao menos 8 caracteres, com maiúscula, minúscula, número e símbolo";

export const passwordRequirements = (value: string) => [
  { label: "8 caracteres ou mais", met: value.length >= 8 },
  { label: "Letra maiúscula", met: /[A-Z]/.test(value) },
  { label: "Letra minúscula", met: /[a-z]/.test(value) },
  { label: "Número", met: /\d/.test(value) },
  { label: "Símbolo", met: /[^A-Za-z0-9]/.test(value) },
];

export const passwordField: FieldValidator = (value) => {
  if (!value || isLocalApi()) return null;

  return value.length >= 8 &&
    /[a-z]/.test(value) &&
    /[A-Z]/.test(value) &&
    /\d/.test(value) &&
    /[^A-Za-z0-9]/.test(value)
    ? null
    : strongPasswordMessage;
};

